import { CdkStep } from '@angular/cdk/stepper';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  effect,
  inject,
  input,
  output
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { expand } from '@pc/animations';
import { ButtonComponent } from '@pc/button';
import { IconComponent } from '@pc/icon';
import { SeparatorComponent } from '@pc/separator';
import { ComponentSizesEnum, PcButtonIcon } from '@pc/common';
import { StepData } from '../models/step-data.interface';
import { StepState } from '../models/step-state.enum';
import { StepViewComponent } from '../step-view/step-view.component';

@Component({
  selector: 'pc-vertical-stepper',
  standalone: true,
  imports: [CommonModule, IconComponent, StepViewComponent, ButtonComponent, SeparatorComponent, TranslateModule],
  templateUrl: './vertical-stepper.component.html',
  styleUrl: './vertical-stepper.component.scss',
  animations: [expand]
})
export class VerticalStepperComponent implements OnInit {
  @Input({ required: true }) progressSteps!: QueryList<CdkStep>;
  @Input() isDevMode = false;
  @Input() accessibilityStatementText = '';

  @Output() requestedStepSelected = new EventEmitter<number>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  public onAccessibilityStatementClicked = output<void>();

  public currentStep = input<number>(0);
  public headerIcon = input<string>('');
  public isStepClickable = input.required<(i: number) => boolean>();
  public isOpen = false;
  public icon = PcButtonIcon.DOWN_CHEVRON;
  public closeIcon = PcButtonIcon.X_CLOSE;
  public PcButtonIcon = PcButtonIcon;
  public steps: StepData[] = [];
  public cdr = inject(ChangeDetectorRef);
  public currentStepName = '';

  protected readonly ComponentSizesEnum = ComponentSizesEnum;

  private destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      this.changeCurrentStep(this.currentStep(), false);
    });
  }

  ngOnInit(): void {
    this.progressSteps.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.steps = this.mapStepsToStepData(this.progressSteps.toArray(), this.currentStep());
      this.updateCurrentStep();
    });
  }

  toggleStepsDropdown() {
    this.isOpen = !this.isOpen;
    this.icon = this.isOpen ? PcButtonIcon.TOGGLE_UP : PcButtonIcon.DOWN_CHEVRON;
  }

  public onStepClick(stepIndex: number): void {
    if (!this.isStepClickable()(stepIndex)) return;

    this.changeCurrentStep(stepIndex);
  }

  public changeCurrentStep(stepIndex: number, triggerChange = true): void {
    if (this.steps.length === 0) return;
    const arrayIndex = stepIndex;

    this.steps = this.steps.map((step, index) => {
      return {
        ...step,
        state: index < arrayIndex ? StepState.COMPLETED : index === arrayIndex ? StepState.CURRENT : StepState.FUTURE
      };
    });

    triggerChange ? this.requestedStepSelected.emit(stepIndex) : this.cdr.markForCheck();
    this.updateCurrentStep();
  }

  public updateCurrentStep(): void {
    if (this.currentStep() > this.steps.length - 1) return;
    this.currentStepName = this.steps[this.currentStep()].name;
  }

  public mapStepsToStepData(steps: CdkStep[], selectedIndex: number): StepData[] {
    return steps.map((step, index) => {
      let state: StepState;

      if (index < selectedIndex) {
        state = StepState.COMPLETED;
      } else if (index === selectedIndex) {
        state = StepState.CURRENT;
      } else {
        state = StepState.FUTURE;
      }

      return {
        name: step.label,
        state: state
      };
    });
  }

  public accessibilityStatementClick(): void {
    this.onAccessibilityStatementClicked.emit();
  }
}
